"use client";

import { useEffect, useState } from "react";
import type { PortalReviewsResponse } from "../lib/clientPortalApi";
import { PortalReviewRating } from "./PortalReviewRating";
import { usePortalPreferences } from "./PortalPreferences";
import { usePortalTranslations } from "../i18n/client";
import styles from "./PortalReviewSummary.module.css";

type Summary = Pick<PortalReviewsResponse, "averageRating" | "total">;
type SummaryState = { slug: string; summary: Summary | null; loading: boolean };

export function PortalReviewSummary({ portalSlug, publicSummary }: { portalSlug: string; publicSummary?: Summary | null }) {
  const t = usePortalTranslations();
  const { locale } = usePortalPreferences();
  const [state, setState] = useState<SummaryState | null>(null);
  const summary = publicSummary ?? (state?.slug === portalSlug ? state.summary : null);
  const loading = !publicSummary && (state?.slug !== portalSlug || state.loading);

  useEffect(() => {
    if (publicSummary) return;
    const controller = new AbortController();
    setState({ slug: portalSlug, summary: null, loading: true });

    fetch(`/api/client-portals/${encodeURIComponent(portalSlug)}/reviews?limit=1&offset=0`, {
      signal: controller.signal,
      cache: "no-store",
    })
      .then(async (response) => {
        if (!response.ok) throw new Error("Reviews unavailable");
        return response.json() as Promise<PortalReviewsResponse>;
      })
      .then(({ averageRating, total }) => {
        if (!controller.signal.aborted) setState({ slug: portalSlug, summary: { averageRating, total }, loading: false });
      })
      .catch(() => {
        if (!controller.signal.aborted) setState({ slug: portalSlug, summary: null, loading: false });
      });

    return () => controller.abort();
  }, [portalSlug, publicSummary]);

  const ratedSummary = summary !== null && summary.total > 0 && summary.averageRating !== null
    ? { rating: summary.averageRating, total: summary.total }
    : null;
  const rating = ratedSummary ? new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(ratedSummary.rating) : "";
  const reviews = ratedSummary ? t(
    ratedSummary.total === 1 ? "portal.reviews.countOne" : ratedSummary.total >= 2 && ratedSummary.total <= 4 ? "portal.reviews.countFew" : "portal.reviews.countMany",
    {count: new Intl.NumberFormat(locale).format(ratedSummary.total)},
  ) : "";
  const label = ratedSummary
    ? t("portal.reviews.summary", {rating, reviews})
    : summary?.total === 0
      ? t("portal.reviews.unrated")
      : t("portal.reviews.show");

  return <a className={styles.link} href={`/${encodeURIComponent(portalSlug)}/reviews`} aria-label={label}>
    {summary || loading ? <PortalReviewRating rating={ratedSummary?.rating ?? 0} decorative /> : null}
    {ratedSummary ? <>
      <span className={styles.score}>{rating} <span>/ 5</span></span>
      <span className={styles.count}>({reviews})</span>
    </> : <span className={styles.prompt}>
      {loading ? t("portal.reviews.loading") : summary?.total === 0 ? t("portal.reviews.writeFirst") : t("portal.reviews.show")}
    </span>}
    <svg className={styles.arrow} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true"><path d="m9 5 7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
  </a>;
}
